import { supabase } from './supabase'
import { createMilestoneInvoice } from './createMilestoneInvoice'
import { fetchMilestoneForInvoice } from './readyToBill'
import type { ReadyToBillItem } from './readyToBill'
import { ActivityTypes, logActivity } from '../utils/activityLogger'

function invoiceNumber(): string {
  return `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`
}

async function createInvoiceShell(params: {
  customer_id: string
  property_id?: string | null
  contract_id?: string | null
  billing_milestone_id?: string | null
  operational_division_id?: string | null
  period_start?: string
  period_end?: string
  amount: number
  notes?: string
  payment_terms?: number
}) {
  const paymentTermsDays = params.payment_terms || 30
  const dueDate = new Date()
  dueDate.setDate(dueDate.getDate() + paymentTermsDays)
  const num = invoiceNumber()

  const { data, error } = await supabase
    .from('invoices')
    .insert({
      invoice_number: num,
      customer_id: params.customer_id,
      property_id: params.property_id || null,
      contract_id: params.contract_id || null,
      billing_milestone_id: params.billing_milestone_id || null,
      operational_division_id: params.operational_division_id || null,
      period_start: params.period_start || null,
      period_end: params.period_end || null,
      subtotal: params.amount,
      total: params.amount,
      balance_due: params.amount,
      amount_paid: 0,
      status: 'draft',
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: dueDate.toISOString().split('T')[0],
      payment_terms: paymentTermsDays,
      notes: params.notes || null,
    })
    .select()
    .single()

  if (error) throw error
  return data
}

export async function generateInvoicesFromReadyItems(
  items: ReadyToBillItem[]
): Promise<{ created: number; errors: string[] }> {
  let created = 0
  const errors: string[] = []

  const byCustomer = new Map<string, ReadyToBillItem[]>()
  for (const item of items.filter((i) => !i.blocked)) {
    if (item.kind === 'milestone') {
      try {
        const ctx = await fetchMilestoneForInvoice(item.milestone_id!)
        if (!ctx) throw new Error('Milestone not found')
        await createMilestoneInvoice(ctx.milestone, ctx.contract)
        created++
      } catch (e) {
        errors.push(`${item.label}: ${(e as Error).message}`)
      }
      continue
    }
    const list = byCustomer.get(item.customer_id) || []
    list.push(item)
    byCustomer.set(item.customer_id, list)
  }

  for (const group of Array.from(byCustomer.values())) {
    const jobItems = group.filter((i: ReadyToBillItem) => i.kind === 'completed_job')
    const periodItems = group.filter((i: ReadyToBillItem) => i.kind === 'contract_period')

    for (const period of periodItems) {
      try {
        const inv = await createInvoiceShell({
          customer_id: period.customer_id,
          property_id: period.property_id,
          contract_id: period.contract_id,
          operational_division_id: period.operational_division_id,
          period_start: period.period_start,
          period_end: period.period_end,
          amount: period.amount,
          notes: period.label,
        })
        await supabase.from('invoice_items').insert({
          invoice_id: inv.id,
          description: period.label,
          quantity: 1,
          unit_price: period.amount,
          total: period.amount,
          display_order: 0,
        })
        await logActivity({
          activity_type: ActivityTypes.INVOICE_CREATED,
          entity_type: 'customer',
          entity_id: period.customer_id,
          description: `Invoice ${inv.invoice_number} created for contract period`,
          metadata: { invoice_id: inv.id, contract_id: period.contract_id },
        })
        created++
      } catch (e) {
        errors.push(`${period.label}: ${(e as Error).message}`)
      }
    }

    const batchJobs = jobItems.filter((j: ReadyToBillItem) => {
      if (!j.contract_id) return true
      return false
    })
    const perJobItems = jobItems.filter((j: ReadyToBillItem) => j.contract_id)

    const createJobInvoice = async (jobItem: ReadyToBillItem, lineItems: any[]) => {
      const inv = await createInvoiceShell({
        customer_id: jobItem.customer_id,
        property_id: jobItem.property_id,
        contract_id: jobItem.contract_id,
        operational_division_id: jobItem.operational_division_id,
        amount: jobItem.amount,
        notes: jobItem.label,
      })
      let order = 0
      if (lineItems.length > 0) {
        for (const li of lineItems) {
          const qty = Number(li.actual_quantity ?? li.quantity ?? 1)
          const unit = Number(li.unit_price || 0)
          const total = li.is_variable ? qty * unit : Number(li.total_price || qty * unit)
          await supabase.from('invoice_items').insert({
            invoice_id: inv.id,
            job_id: jobItem.job_id,
            description: li.description,
            quantity: qty,
            unit_price: unit,
            total,
            display_order: order++,
          })
          await supabase
            .from('job_line_items')
            .update({ billing_status: 'billed', billed_amount: total })
            .eq('id', li.id)
        }
      } else {
        await supabase.from('invoice_items').insert({
          invoice_id: inv.id,
          job_id: jobItem.job_id,
          description: jobItem.label,
          quantity: 1,
          unit_price: jobItem.amount,
          total: jobItem.amount,
          display_order: 0,
        })
      }
      await logActivity({
        activity_type: ActivityTypes.INVOICE_CREATED,
        entity_type: 'customer',
        entity_id: jobItem.customer_id,
        description: `Invoice ${inv.invoice_number} created for ${jobItem.label}`,
        metadata: { invoice_id: inv.id, job_id: jobItem.job_id },
      })
      created++
    }

    if (batchJobs.length > 0) {
      try {
        const total = batchJobs.reduce((s: number, j: ReadyToBillItem) => s + j.amount, 0)
        const first = batchJobs[0]
        const inv = await createInvoiceShell({
          customer_id: first.customer_id,
          property_id: first.property_id,
          operational_division_id: first.operational_division_id,
          amount: total,
          notes: `Batch invoice — ${batchJobs.length} job(s)`,
        })
        let order = 0
        for (const j of batchJobs) {
          const { data: lineItems } = await supabase
            .from('job_line_items')
            .select('*')
            .eq('job_id', j.job_id!)
          if (lineItems && lineItems.length > 0) {
            for (const li of lineItems) {
              const qty = Number(li.actual_quantity ?? li.quantity ?? 1)
              const unit = Number(li.unit_price || 0)
              const lineTotal = li.is_variable ? qty * unit : Number(li.total_price || qty * unit)
              await supabase.from('invoice_items').insert({
                invoice_id: inv.id,
                job_id: j.job_id,
                description: li.description,
                quantity: qty,
                unit_price: unit,
                total: lineTotal,
                display_order: order++,
              })
              await supabase
                .from('job_line_items')
                .update({ billing_status: 'billed', billed_amount: lineTotal })
                .eq('id', li.id)
            }
          } else {
            await supabase.from('invoice_items').insert({
              invoice_id: inv.id,
              job_id: j.job_id,
              description: j.label,
              quantity: 1,
              unit_price: j.amount,
              total: j.amount,
              display_order: order++,
            })
          }
        }
        created++
      } catch (e) {
        errors.push(`Batch jobs: ${(e as Error).message}`)
      }
    }

    for (const j of perJobItems) {
      try {
        const { data: lineItems } = await supabase
          .from('job_line_items')
          .select('*')
          .eq('job_id', j.job_id!)
        await createJobInvoice(j, lineItems || [])
      } catch (e) {
        errors.push(`${j.label}: ${(e as Error).message}`)
      }
    }
  }

  return { created, errors }
}
